import accents from 'remove-accents';
import React, { Component } from 'react';
import { PropTypes as RPT } from 'prop-types';
import Fuse from 'fuse.js';
import Error from './Error.react';
import Letter from './Letter.react';
import LettersFilter from './LettersFilter.react';
import Filter from './Filter.react';
import Term from './Term.react';
import 'whatwg-fetch';

const calculateDataByLetter = data => data.reduce(
  (acc, term) => {
    const letter = accents.remove(term.title[0].toUpperCase());
    const key = letter.match(/[A-Z]/i) ? letter : '0-99';

    return { ...acc, [key]: (acc[key] || []).concat(term) };
  },
  {},
);


const calculateDataByFields = data => data.reduce(
  (acc, term) => {
    const { field } = term;
    return { ...acc, [field]: (acc[field] || []).concat(term) };
  },
  {},
);

const calculateActiveLetter = (activeLetter, data) => {
  const letters = Object.keys(calculateDataByLetter(data)).sort();
  return letters.indexOf(activeLetter) !== -1 ? activeLetter : letters[0];
};

export default class Dictionary extends Component {
  static propTypes = {
    apiUrl: RPT.string.isRequired,
    localization: RPT.object,
    field: RPT.string,
  }

  constructor() {
    super();
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  state = {
    activeLetter: null,
    activeField: null,
    _sourceData: [],
    data: [],
    fields: [],
    dataByFields: null,
    searchTerm: null,
    error: null,
  }

  componentWillMount() {
    window.fetch(this.props.apiUrl)
      .then(response => response.json())
      .then((data) => {
        this.handleTerms(data.terms);
        this.setFields(data.fields);
      })
      .catch(error => this.handleError(error));
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  activateLetter(event, letter) {
    this.setState({ activeLetter: letter });
  }

  searchTermChanged({ target: { value } }) {
    const { activeLetter, _sourceData } = this.state;
    const options = {
      threshold: 0.25,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: ['title', 'en_title'],
    };

    const fuse = new Fuse(_sourceData, options);

    if (value && value.length > 0) {
      const searchedData = fuse.search(value);

      const newActiveLetter = calculateActiveLetter(activeLetter, searchedData);
      this.setState({
        activeLetter: newActiveLetter,
        searchTerm: value,
        data: searchedData,
        dataByFields: calculateDataByFields(searchedData),
      });
    } else {
      const newActiveLetter = calculateActiveLetter(activeLetter, _sourceData);
      this.setState({
        activeLetter: newActiveLetter,
        searchTerm: value,
        data: _sourceData,
        dataByFields: calculateDataByFields(_sourceData),
      });
    }
  }

  getFieldName(fieldId) {
    const id = typeof fieldId === 'string' ? parseInt(fieldId, 10) : fieldId;
    const field = this.state.fields.find(f => f.id === id);
    return field ? field.title : '';
  }

  activateField(event, field) {
    if (event) {
      event.preventDefault();
    }
    const { activeLetter } = this.state;
    // clicked on same field, clear selection
    if (this.state.activeField === field) { this.setState({ activeField: null }); } else {
      const newActiveLetter = calculateActiveLetter(activeLetter, this.state.dataByFields[field]);
      this.setState({ activeLetter: newActiveLetter, activeField: field });
    }
  }

  handleKeyDown(event) {
    if (!$('input').is(':focus')) {
      const char = (event || window.event).key.toUpperCase();

      const { activeField, data, dataByFields } = this.state;
      const activeTerms = activeField
        ? dataByFields[activeField]
        : data;

      const dataByLetter = calculateDataByLetter(activeTerms || []);
      const letters = Object.keys(dataByLetter).sort();
      if (letters.indexOf(char) !== -1) { this.activateLetter(null, char); }
    }
  }

  setFields(data) {
    this.setState({ fields: data });
  }

  handleTerms(data) {
    const dataByFields = calculateDataByFields(data);

    const activeLetter = Object.keys(calculateDataByLetter(data)).sort()[1];
    this.setState({
      _sourceData: data, activeLetter, data, dataByFields,
    });
    if (this.props.field) { this.activateField(null, this.props.field); }
  }

  handleError(error) {
    this.setState({ error });
  }

  render() {
    if (this.state.error) return <Error />;

    const { localization } = this.props;
    const {
      activeLetter, activeField, data, dataByFields, searchTerm,
    } = this.state;
    const activateLetter = this.activateLetter.bind(this);
    const getFieldName = this.getFieldName.bind(this);
    const activateField = this.activateField.bind(this);
    const searchTermChanged = this.searchTermChanged.bind(this);

    const activeTerms = activeField
      ? dataByFields[activeField]
      : data;

    const dataByLetter = calculateDataByLetter(activeTerms || []);
    const letters = Object.keys(dataByLetter).sort();
    const activeLetterTerms = dataByLetter[activeLetter] || [];

    const totalTerms = Object.keys(dataByLetter).reduce((acc, val) => (acc + dataByLetter[val].length), 0);
    const isManyTerms = totalTerms > 99;
    const isSearching = searchTerm && searchTerm.length > 0;
    const isGroupedView = isSearching && !isManyTerms;
    return (
      <div>
        <Filter
          activeField={activeField}
          activateField={activateField}
          dataByFields={dataByFields}
          searchTerm={searchTerm}
          searchTermChanged={searchTermChanged}
          getFieldName={getFieldName}
          localization={localization}
        />

        { !isGroupedView && (
          <LettersFilter
              activateLetter={activateLetter}
              activeLetter={activeLetter}
              letters={letters}
          />
        )}

        <div className="letter-wrapper">
          { isGroupedView
            ? <div className="product-boxes">
              { Object.keys(dataByLetter).map(letter => (
                dataByLetter[letter].map(term => (
                <Term
                  key={term.id}
                  title={term.title}
                  field={getFieldName(term.field)}
                  url={term.url}
                />
                ))
              ))}
            </div>
            : <Letter
              letter={activeLetter}
              terms={activeLetterTerms}
              getFieldName={getFieldName}
            />
          }
        </div>
      </div>
    );
  }
}
